import { Star, Quote } from "lucide-react";
import { TESTIMONIALS, GOOGLE_BUSINESS_URL } from "@/data/testimonials";

// Google "G" logo — inline SVG so we don't depend on external assets.
function GoogleLogo({ className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.72 1.22 9.22 3.6l6.9-6.9C36.16 2.24 30.52 0 24 0 14.62 0 6.51 5.38 2.56 13.22l8.06 6.25C12.5 13.72 17.77 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.42-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-8.06-6.25C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-8.01 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

function StarRow({ n = 5 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${n} de 5 estrellas`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i <= n ? "fill-amber-400 text-amber-400" : "text-brand-line"}`}
        />
      ))}
    </div>
  );
}

function initials(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
}

export default function Testimonials({
  eyebrow = "Familias que ya han viajado con nosotros",
  title = "Lo que dicen quienes ya han pisado la nieve con nosotros.",
  intro = "Estas opiniones no son inventadas ni pedidas. Son reseñas verificadas, publicadas por familias reales en nuestra ficha de Google Business.",
  limit,
}) {
  const items = limit ? TESTIMONIALS.slice(0, limit) : TESTIMONIALS;

  return (
    <section className="section-y bg-brand-bg" data-testid="testimonials-section">
      <div className="container-prose">
        <div className="max-w-3xl mb-10">
          <p className="overline">{eyebrow}</p>
          <h2 className="h2-display mt-3">{title}</h2>
          <p className="mt-5 text-brand-muted text-base md:text-lg">{intro}</p>
          <a
            href={GOOGLE_BUSINESS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-badge"
            className="mt-6 inline-flex items-center gap-3 rounded-full border border-brand-line bg-white/70 px-4 py-2 text-sm text-brand-primary hover:border-brand-accent hover:bg-white transition-colors"
            aria-label="Ver reseñas verificadas en Google"
          >
            <GoogleLogo className="h-4 w-4" />
            <span className="font-medium">Más de 100 reseñas</span>
            <span className="text-brand-muted">· de clientes reales en Google</span>
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((t, i) => (
            <article
              key={`${t.name}-${i}`}
              data-testid={`testimonial-${i}`}
              className="card-soft p-6 md:p-7 flex flex-col relative"
            >
              <Quote className="absolute top-5 right-5 h-6 w-6 text-brand-warm/40" aria-hidden="true" />
              <StarRow n={t.rating} />
              <p className="mt-4 font-cormorant italic text-lg md:text-xl text-brand-primary leading-snug">
                “{t.text}”
              </p>
              <div className="mt-6 pt-5 border-t border-brand-line flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-brand-accent/10 text-brand-accent font-outfit font-semibold flex items-center justify-center text-sm shrink-0">
                  {initials(t.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-outfit font-semibold text-brand-primary text-sm truncate">
                    {t.name}
                  </p>
                  <p className="text-xs text-brand-muted truncate">
                    {t.trip} · {t.date_label}
                  </p>
                </div>
                <GoogleLogo className="h-4 w-4 shrink-0" />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
